// fin-core / centralisation — C5: what macompta receives for a period (journal CTPMS), and the period's
// figures read from it. Pure: types and functions, no I/O (PMS-LEDGER-CENTRALISATION-BRIEF §5f).

export type CentralisationKind = 'sales' | 'payments' | 'remise' | 'entry';

export interface CentralisationLine {
  readonly lineNo: number;
  /** macompta's journal the line goes to: VTPMS, VTLS, BKPMS, BKLS. */
  readonly targetJournal: string;
  /** macompta's « N° pièce »: CT-<month>-VT, REM-<n>, AMEX-<n>, BQ-<day>-<account>, or the entry's own. */
  readonly piece: string;
  readonly entryDate: string;
  readonly account: string;
  readonly accountLabel: string | null;
  readonly debitCents: number;
  readonly creditCents: number;
  readonly label: string;
  readonly kind: CentralisationKind | string;
}

export interface Centralisation {
  readonly id: number;
  readonly version: number;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly issuedAt: string;
  /** Sealed (the VAT return filed): this version is final. */
  readonly sealed: boolean;
  readonly lines: readonly CentralisationLine[];
}

export interface AccountAmount {
  readonly account: string;
  readonly label: string | null;
  readonly amountCents: number;
}

export interface CentralisationSummary {
  /** Revenue HT per account (706, 707, 708…), credit-positive. */
  readonly revenue: readonly AccountAmount[];
  /** VAT collected per account (4457x), credit-positive. */
  readonly vat: readonly AccountAmount[];
  /** Taxe de séjour collected (4476x), credit-positive. */
  readonly touristTax: readonly AccountAmount[];
  /** Customer payments per tender (5112x), debit-positive: what was taken. */
  readonly takings: readonly AccountAmount[];
  /** What the bank was credited (512xxxxx reflets), from the remises and transfers. */
  readonly bank: readonly AccountAmount[];
  /** Commissions and fees (627xx, 622xx, 658xx), debit-positive. */
  readonly fees: readonly AccountAmount[];
  /** How many entries macompta receives per journal (an entry = one piece on one day). */
  readonly entriesPerJournal: Readonly<Record<string, number>>;
  readonly debitCents: number;
  readonly creditCents: number;
}

const startsWithAny = (account: string, prefixes: readonly string[]) => prefixes.some((p) => account.startsWith(p));

function byAccount(
  lines: readonly CentralisationLine[],
  keep: (l: CentralisationLine) => boolean,
  sign: 1 | -1,
): AccountAmount[] {
  const m = new Map<string, { label: string | null; v: number }>();
  for (const l of lines) {
    if (!keep(l)) continue;
    const cur = m.get(l.account) ?? { label: l.accountLabel, v: 0 };
    cur.v += sign * (l.debitCents - l.creditCents);
    m.set(l.account, cur);
  }
  return [...m.entries()]
    .filter(([, x]) => x.v !== 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([account, x]) => ({ account, label: x.label, amountCents: x.v }));
}

/** The period's figures, read from exactly what macompta receives. */
export function centralisationSummary(lines: readonly CentralisationLine[]): CentralisationSummary {
  const notPayments = (l: CentralisationLine) => l.kind !== 'payments';
  const entries = new Map<string, Set<string>>();
  let debit = 0;
  let credit = 0;
  for (const l of lines) {
    const set = entries.get(l.targetJournal) ?? new Set<string>();
    set.add(`${l.piece}|${l.entryDate}`);
    entries.set(l.targetJournal, set);
    debit += l.debitCents;
    credit += l.creditCents;
  }
  return {
    revenue: byAccount(lines, (l) => notPayments(l) && startsWithAny(l.account, ['70', '75', '77']), -1),
    vat: byAccount(lines, (l) => notPayments(l) && l.account.startsWith('4457'), -1),
    touristTax: byAccount(lines, (l) => notPayments(l) && l.account.startsWith('4476'), -1),
    takings: byAccount(lines, (l) => l.kind === 'payments' && l.account.startsWith('5112'), 1),
    bank: byAccount(lines, (l) => l.kind === 'remise' && l.account.startsWith('512'), 1),
    fees: byAccount(lines, (l) => startsWithAny(l.account, ['627', '622', '658']), 1),
    entriesPerJournal: Object.fromEntries([...entries.entries()].map(([j, s]) => [j, s.size])),
    debitCents: debit,
    creditCents: credit,
  };
}

/** The total of a list of amounts. */
export const sumAmounts = (xs: readonly AccountAmount[]): number => xs.reduce((s, x) => s + x.amountCents, 0);
